import React, { useEffect, useContext, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//functions
import {
  _t,
  getCookie,
  tableLoading,
  customStyle,
} from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";
import { useCustomHooks } from "../../../../../functions/Hooks";

//3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

//pages & includes
import ManageSidebar from "../ManageSidebar";

//context consumer
import { RestaurantContext } from "../../../../../contexts/Restaurant";

const animatedComponents = makeAnimated();

const StockIssuanceDeptAction = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { roundOffStock, qtyAmountPoints } = useCustomHooks();

  let {
    loading,
    setLoading,
    branchForSearch,
    getBranch,
  } = useContext(RestaurantContext);

  // States hook here
  const [branchDepartments, setBranchDepartments] = useState([]);
  const [loadingDept, setLoadingDept] = useState(false);

  // new stock issuance
  const [newStockIssuance, setNewStockIssuance] = useState({
    document_no: "",
    ref_identity: "",
    branch: null,
    fdept: null,
    tdept: null,
    remarks: "",
  });

  // new Row
  const [newRow, setNewRow] = useState([]);
  // datepicker
  const [documentDate, setDocumentDate] = useState(new Date());

  const [storeReqDoc, setStoreReqDoc] = useState([]);

  // Fetch branch-wise departments
  const getBranchWiseDepartments = async (branchId) => {
    if (!branchId) {
      setBranchDepartments([]);
      return;
    }
    setLoadingDept(true);
    const url = BASE_URL + "/settings/branch-wise-department/" + branchId;
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      const depts = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data?.departments)
        ? res.data.departments
        : [];
      setBranchDepartments(depts);
    } catch (error) {
      console.log("Error fetching branch departments:", error);
      setBranchDepartments([]);
    } finally {
      setLoadingDept(false);
    }
  };

  const handleSetEditView = (editIssuance) => {
    const docDate = new Date(editIssuance.document_date || editIssuance.date);
    const branch =
      editIssuance.branch ||
      (branchForSearch &&
        branchForSearch.find((b) => b.id === editIssuance.branch_id)) ||
      (editIssuance.branch_id
        ? { id: editIssuance.branch_id, name: editIssuance.branch_name }
        : null);

    const fdept =
      editIssuance.from_department ||
      editIssuance.from_dept ||
      (editIssuance.from_dept_id
        ? { id: editIssuance.from_dept_id, name: editIssuance.from_dept_name }
        : null);

    const tdept =
      editIssuance.to_department ||
      editIssuance.to_dept ||
      (editIssuance.to_dept_id
        ? { id: editIssuance.to_dept_id, name: editIssuance.to_dept_name }
        : null);

    setNewStockIssuance((prev) => ({
      ...prev,
      is_post: editIssuance.is_post,
      document_no:
        editIssuance.document_identity || editIssuance.document_no || "",
      branch: branch,
      fdept: fdept,
      tdept: tdept,
      ref_identity: {
        id: null,
        document_identity: editIssuance.ref_identity,
      },
      remarks: editIssuance.remarks || "",
    }));
    setDocumentDate(docDate);

    const branchId = editIssuance.branch?.id || editIssuance.branch_id;
    if (branchId) {
      getBranchWiseDepartments(branchId);
    }

    const details =
      editIssuance.stock_issuance_department_detail ||
      editIssuance.stock_issuance_dept_detail ||
      editIssuance.stock_issuance_detail ||
      editIssuance.details ||
      [];

    if (details && details.length > 0) {
      const rowArr = details.map((item) => ({
        item: {
          id: item.item_id,
          name: item.item_name,
        },
        unit: {
          id: item.unit_id,
          name: item.unit_name,
        },
        item_id: item.item_id,
        item_name: item.item_name,
        unit_id: item.unit_id,
        unit_name: item.unit_name,
        qty: roundOffStock(item.qty),
        requisition_qty: roundOffStock(item.requisition_qty || item.qty),
        max_qty: roundOffStock(item.requisition_qty || item.qty),
        stock_qty: roundOffStock(item.stock_qty || 0),
        base_unit: item.base_unit || item.unit_id,
        base_qty:
          item.base_qty !== undefined && item.base_qty !== null
            ? item.base_qty
            : item.qty,
        conversion_qty: item.conversion_qty || 1,
        cost_price: parseFloat(item.cost_price || 0),
      }));
      setNewRow(rowArr);
    }
  };

  // Get Specific item for edit
  const getStockIssuanceItem = async () => {
    setLoading(true);
    const url = BASE_URL + "/settings/stock-issuance-dept/" + parseInt(id);
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      const data = res?.data?.data || res?.data;
      if (data) {
        handleSetEditView(data);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!branchForSearch && getBranch) {
      getBranch();
    }
    if (id) {
      getStockIssuanceItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // store req doc no
  const getStoreReqDoc = async (toDeptId, fromDeptId) => {
    const fId = fromDeptId || newStockIssuance.fdept?.id;
    if (!fId || !toDeptId) return;

    const url = BASE_URL + "/settings/get-requistion-dept-list";
    const formData = {
      from_dept_id: fId,
      to_dept_id: toDeptId,
      ...(newStockIssuance.branch?.id && {
        branch_id: newStockIssuance.branch.id,
      }),
    };

    try {
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res && res.data) {
        setStoreReqDoc(
          res.data.stock_requisitions_dept ||
          res.data.stock_requisitions ||
          (Array.isArray(res.data) ? res.data : res.data.data || [])
        );
      }
    } catch (error) {
      console.log("check error", error);
    }
  };

  // store req items
  const getStoreReqItems = async (ref_doc) => {
    if (!ref_doc || !ref_doc.id) return;

    const url = BASE_URL + "/settings/get-requistion-dept-details-list";
    const branchId = newStockIssuance.branch?.id || newStockIssuance.branch_id;
    const formData = {
      id: ref_doc.id,
      ...(branchId && {
        branch_id: branchId,
      }),
    };

    try {
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res && res.data) {
        const itemsData = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
        const rowArr = itemsData.map((item) => {
          const itemObj = item.item
            ? item.item
            : { id: item.item_id, name: item.item_name };
          const unitObj = item.unit
            ? item.unit
            : { id: item.unit_id, name: item.unit_name };
          return {
            ...item,
            item: itemObj,
            item_id: itemObj?.id || item.item_id,
            item_name: itemObj?.name || item.item_name,
            unit: unitObj,
            unit_id: unitObj?.id || item.unit_id,
            unit_name: unitObj?.name || item.unit_name,
            qty: roundOffStock(item.qty),
            requisition_qty: roundOffStock(item.qty),
            max_qty: Number(item.qty),
            stock_qty: roundOffStock(item.stock_qty || 0),
            base_unit: item.base_unit || unitObj?.id || item.unit_id,
            base_qty:
              item.base_qty !== undefined && item.base_qty !== null
                ? item.base_qty
                : item.qty,
            conversion_qty: item.conversion_qty || 1,
            sell_price: item.sell_price || "0.0000",
            cost_price: parseFloat(item.cost_price || item.sell_price || 0),
          };
        });
        setNewRow(rowArr);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Store req items request canceled:", error.message);
        return;
      }
      console.log("check error", error);
    }
  };

  // set each item qty
  const handleItemQty = (index, e) => {
    const val = e.target.value;
    if (val === "" || val === null) {
      newRow[index].qty = "";
      setNewRow([...newRow]);
      return;
    }
    const entered = parseFloat(val);
    const stockQty = parseFloat(newRow[index].stock_qty);
    const reqQty = parseFloat(newRow[index].max_qty || newRow[index].requisition_qty);

    if (
      (!isNaN(stockQty) && entered > stockQty) ||
      (!isNaN(reqQty) && entered > reqQty)
    ) {
      toast.error("Your qty should not be greater than the stock/req. qty.", {
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
    } else {
      newRow[index].qty = val;
      setNewRow([...newRow]);
    }
  };

  // Branch change
  const handleSetBranch = (branch) => {
    setNewStockIssuance((prev) => ({
      ...prev,
      branch: branch || null,
      fdept: null,
      tdept: null,
      ref_identity: "",
    }));
    setBranchDepartments([]);
    setStoreReqDoc([]);
    setNewRow([]);
    if (branch && branch.id) {
      getBranchWiseDepartments(branch.id);
    }
  };

  // From Dept change
  const handleSetDept = (dept) => {
    setNewStockIssuance((prev) => ({
      ...prev,
      fdept: dept || null,
      tdept: null,
      ref_identity: "",
    }));
    setStoreReqDoc([]);
    setNewRow([]);
  };

  // To Dept change
  const handleSetToDept = (tdept) => {
    setNewStockIssuance((prev) => ({
      ...prev,
      tdept: tdept || null,
      ref_identity: "",
    }));
    setStoreReqDoc([]);
    setNewRow([]);
    if (tdept && tdept.id && newStockIssuance.fdept?.id) {
      getStoreReqDoc(tdept.id, newStockIssuance.fdept.id);
    }
  };

  // Select Ref change
  const handleSetDocId = (ref_identity) => {
    setNewStockIssuance((prev) => ({ ...prev, ref_identity: ref_identity || "" }));
    if (ref_identity) {
      getStoreReqItems(ref_identity);
    } else {
      setNewRow([]);
    }
  };

  const handleRemarks = (e) => {
    const value = e.target.value;
    setNewStockIssuance((prev) => ({ ...prev, remarks: value }));
  };

  // post req of stock item update
  const stockItemAxios = async () => {
    const d = new Date(documentDate);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    const itemDetails = [];
    let isQtyOverflow = false;
    let isInvalidQty = false;

    newRow.forEach((newIssuance) => {
      const enteredQty = parseFloat(newIssuance.qty);
      const stockQty = parseFloat(newIssuance.stock_qty);
      const reqQty = parseFloat(newIssuance.max_qty || newIssuance.requisition_qty);

      if (isNaN(enteredQty) || enteredQty <= 0) {
        isInvalidQty = true;
      }

      if (
        (!isNaN(stockQty) && enteredQty > stockQty) ||
        (!isNaN(reqQty) && enteredQty > reqQty)
      ) {
        isQtyOverflow = true;
      }

      const conversion = parseFloat(newIssuance.conversion_qty || 1) || 1;
      const baseQty = enteredQty * conversion;

      itemDetails.push({
        qty: enteredQty,
        item_id: newIssuance.item_id || newIssuance.item?.id,
        item_name: newIssuance.item_name || newIssuance.item?.name,
        unit_id: newIssuance.unit_id || newIssuance.unit?.id,
        unit_name: newIssuance.unit_name || newIssuance.unit?.name,
        base_unit: newIssuance.base_unit || newIssuance.unit_id || newIssuance.unit?.id,
        base_qty: baseQty,
        conversion_qty: conversion,
        stock_qty: parseFloat(newIssuance.stock_qty || 0),
      });
    });

    if (isInvalidQty) {
      toast.error(
        `${_t(t("Please enter a valid quantity greater than 0 for all items"))}`,
        {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        }
      );
      return;
    }

    if (isQtyOverflow) {
      toast.error(
        `${_t(t("Your qty should not be greater than the stock/req. qty."))}`,
        {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        }
      );
      return;
    }

    if (newRow.length === itemDetails.length) {
      setLoading(true);
      const payload = {
        branch_id: newStockIssuance.branch?.id,
        from_dept_id: newStockIssuance.fdept?.id,
        to_dept_id: newStockIssuance.tdept?.id,
        document_date: date,
        ref_identity:
          newStockIssuance.ref_identity?.document_identity ||
          newStockIssuance.ref_identity ||
          "",
        remarks: newStockIssuance.remarks || "",
        items: itemDetails,
      };
      const method = id ? "put" : "post";
      const url =
        BASE_URL + "/settings/stock-issuance-dept" + (id ? `/${id}` : "");
      try {
        await axios[method](url, payload, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });
        setLoading(false);
        toast.success(
          `${_t(
            t(
              id
                ? "Stock issuance has been updated"
                : "Stock issuance has been saved"
            )
          )}`,
          {
            position: "bottom-center",
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            className: "text-center toast-notification",
          }
        );
        history.push("/dashboard/manage/stock/stock-issuance-dept");
      } catch (error) {
        console.log("check error==>", error);
        setLoading(false);
        const errMsg =
          error.response?.data?.message || "Please try again later";
        toast.error(`${_t(t(errMsg))}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      }
    }
  };

  // send to server
  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      Boolean(newStockIssuance.branch) &&
      Boolean(newStockIssuance.fdept) &&
      Boolean(newStockIssuance.tdept) &&
      documentDate !== null &&
      Boolean(
        newStockIssuance.ref_identity?.document_identity ||
          newStockIssuance.ref_identity
      )
    ) {
      if (newRow.length > 0) {
        stockItemAxios();
      } else {
        toast.error(`${_t(t("Please select doc with item"))}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      }
    } else {
      toast.error(`${_t(t("Please fill all the required fields"))}`, {
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {id
            ? newStockIssuance?.is_post
              ? _t(t("View Stock Issuance Dept"))
              : _t(t("Edit Stock Issuance Dept"))
            : _t(t("Add Stock Issuance Dept"))}
        </title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    {/* Loading effect */}
                    {loading === true ? (
                      tableLoading()
                    ) : (
                      <div key="smtp-form">
                        <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                          <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                            <ul className="t-list fk-breadcrumb">
                              <li className="fk-breadcrumb__list">
                                <span className="t-link fk-breadcrumb__link text-capitalize">
                                  {id
                                    ? newStockIssuance?.is_post
                                      ? _t(t("View Stock Issuance Department"))
                                      : _t(t("Edit Stock Issuance Department"))
                                    : _t(t("Add Stock Issuance Department"))}
                                </span>
                              </li>
                            </ul>
                          </div>
                          <div className="col-md-6 col-lg-7">
                            <div className="row gx-3 align-items-center"></div>
                          </div>
                        </div>

                        {/* Form starts here */}
                        <form
                          className="row card p-2 mx-3 mb-5 table-text"
                          onSubmit={handleSubmit}
                          autoComplete="off"
                        >
                          <div className="col-12">
                            <div className="row">
                              <div className="form-group col-4 mt-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="document_no"
                                    className="control-label"
                                  >
                                    {_t(t("Document no"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <input
                                    readOnly
                                    type="text"
                                    className="form-control table-text"
                                    id="document_no"
                                    name="document_no"
                                    value={newStockIssuance.document_no || ""}
                                    placeholder="Auto no"
                                    required
                                  />
                                </div>
                              </div>

                              <div className="form-group col-4 text-right mt-3">
                                <div className="mb-2 text-left">
                                  <label
                                    htmlFor="transfer_date"
                                    className="control-label"
                                  >
                                    {_t(t("Document Date"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <DatePicker
                                    selected={documentDate}
                                    onChange={(date) => setDocumentDate(date)}
                                    peekNextMonth
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    className="form-control table-text"
                                    placeholderText={_t(t("Document date"))}
                                    shouldCloseOnSelect={false}
                                    disabled={newStockIssuance.is_post}
                                  />
                                </div>
                              </div>

                              <div className="form-group mt-3 col-4">
                                <div className="mb-2 text-left">
                                  <label
                                    htmlFor="branch"
                                    className="control-label"
                                  >
                                    {_t(t("Branch"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={branchForSearch ? branchForSearch : []}
                                  components={animatedComponents}
                                  getOptionLabel={(option) => option?.name || option?.branch_name || ""}
                                  getOptionValue={(option) => option?.id || ""}
                                  value={newStockIssuance.branch || null}
                                  isDisabled={id || newStockIssuance.is_post}
                                  classNamePrefix="select"
                                  onChange={handleSetBranch}
                                  maxMenuHeight="200px"
                                  placeholder={
                                    _t(t("Please select branch")) + ".."
                                  }
                                  styles={customStyle}
                                  required
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="form-group mt-2 col-4">
                                <div className="mb-1 text-left">
                                  <label
                                    htmlFor="fdept"
                                    className="control-label text-truncate"
                                  >
                                    {_t(t("From Department"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={Array.isArray(branchDepartments) ? branchDepartments : []}
                                  components={animatedComponents}
                                  getOptionLabel={(option) => option?.name || option?.dept_name || ""}
                                  getOptionValue={(option) => option?.id || ""}
                                  value={newStockIssuance.fdept || null}
                                  isDisabled={!newStockIssuance.branch || id || newStockIssuance.is_post}
                                  isLoading={loadingDept}
                                  classNamePrefix="select"
                                  onChange={handleSetDept}
                                  maxMenuHeight="200px"
                                  placeholder={
                                    !newStockIssuance.branch
                                      ? _t(t("Please select branch first"))
                                      : loadingDept
                                      ? _t(t("Loading..."))
                                      : _t(t("Please select from dept")) + ".."
                                  }
                                  styles={customStyle}
                                  required
                                />
                              </div>

                              <div className="form-group mt-2 col-4">
                                <div className="mb-2">
                                  <label
                                    htmlFor="tdept"
                                    className="control-label"
                                  >
                                    {_t(t("To Department"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={
                                    Array.isArray(branchDepartments)
                                      ? branchDepartments.filter(
                                          (item) =>
                                            item &&
                                            item.id !==
                                            newStockIssuance?.fdept?.id
                                        )
                                      : []
                                  }
                                  components={animatedComponents}
                                  getOptionLabel={(option) => option?.name || option?.dept_name || ""}
                                  getOptionValue={(option) => option?.id || ""}
                                  value={newStockIssuance.tdept || null}
                                  classNamePrefix="select"
                                  onChange={handleSetToDept}
                                  maxMenuHeight="200px"
                                  isDisabled={!newStockIssuance.fdept || id || newStockIssuance.is_post}
                                  styles={customStyle}
                                  placeholder={
                                    !newStockIssuance.fdept
                                      ? _t(t("Please select from dept first"))
                                      : _t(t("Please select to dept")) + ".."
                                  }
                                />
                              </div>

                              <div className="form-group mt-2 col-4">
                                <div className="mb-2">
                                  <label
                                    htmlFor="ref_identity"
                                    className="control-label"
                                  >
                                    {_t(t("Select Document Ref"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={Array.isArray(storeReqDoc) ? storeReqDoc : []}
                                  components={animatedComponents}
                                  getOptionLabel={(option) =>
                                    (option?.document_identity || "") +
                                    (option?.ref_identity
                                      ? " (" + option.ref_identity + ")"
                                      : "")
                                  }
                                  getOptionValue={(option) => option?.id || ""}
                                  value={newStockIssuance.ref_identity || null}
                                  classNamePrefix="select"
                                  onChange={handleSetDocId}
                                  isDisabled={
                                    !newStockIssuance.tdept ||
                                    Boolean(id) ||
                                    newStockIssuance.is_post
                                  }
                                  maxMenuHeight="200px"
                                  styles={customStyle}
                                  placeholder={
                                    !newStockIssuance.tdept
                                      ? _t(t("Please select to dept first"))
                                      : _t(t("Please select doc")) + ".."
                                  }
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="form-group mt-2 col-12">
                                <div className="mb-2">
                                  <label
                                    htmlFor="remarks"
                                    className="control-label"
                                  >
                                    {_t(t("Remarks"))}
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <textarea
                                    className="form-control table-text"
                                    id="remarks"
                                    name="remarks"
                                    onChange={handleRemarks}
                                    value={newStockIssuance.remarks || ""}
                                    placeholder="Remarks"
                                    style={{ height: "40px" }}
                                    disabled={newStockIssuance.is_post}
                                  />
                                </div>
                              </div>
                            </div>

                            <div
                              className="table-responsive mt-3 pr-1"
                              style={{ maxHeight: "23rem", overflow: "auto" }}
                            >
                              <table className="table sticky-table-header table-bordered-sm table-hover">
                                <thead className="align-middle">
                                  <tr>
                                    <th
                                      style={{ minWidth: "150px" }}
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border table_child"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Item Name"))}
                                      </span>
                                    </th>

                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Requisition Qty"))}
                                      </span>
                                    </th>

                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Stock Qty"))}
                                      </span>
                                    </th>

                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Qty"))}
                                      </span>
                                    </th>

                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Unit"))}
                                      </span>
                                    </th>
                                  </tr>
                                </thead>
                                {newRow && newRow.length > 0 ? (
                                  <tbody className="align-middle">
                                    {newRow.map((item, index) => {
                                      return (
                                        <tr
                                          className="align-middle"
                                          key={index}
                                        >
                                          <td className="table-text text-capitalize align-middle text-center">
                                            {item.item?.name || item.item_name}
                                          </td>
                                          <td className="table-text text-capitalize align-middle text-center">
                                            {item.requisition_qty ?? item.qty}
                                          </td>
                                          <td className="table-text text-capitalize align-middle text-center">
                                            {item.stock_qty ?? 0}
                                          </td>

                                          <td className="table-text text-capitalize align-middle text-center">
                                            <input
                                              disabled={newStockIssuance.is_post}
                                              type="number"
                                              step={qtyAmountPoints()}
                                              min="0"
                                              onKeyDown={(evt) =>
                                                [
                                                  "e",
                                                  "E",
                                                  "+",
                                                  "-",
                                                  "ArrowUp",
                                                  "ArrowDown",
                                                ].includes(evt.key) &&
                                                evt.preventDefault()
                                              }
                                              onWheel={(e) => e.target.blur()}
                                              className="form-control text-center table-Input table-text"
                                              onChange={(e) =>
                                                handleItemQty(index, e)
                                              }
                                              value={item?.qty || ""}
                                              placeholder="Qty"
                                              required
                                            />
                                          </td>

                                          <td className="table-text text-capitalize align-middle text-center">
                                            {item.unit?.name || item.unit_name}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                ) : null}
                              </table>
                            </div>

                            <div className="d-flex justify-content-end align-items-center mt-3">
                              <div className="col-3 d-flex justify-content-end">
                                <button
                                  type="button"
                                  className="btn rounded hov-btn mr-3"
                                  onClick={() =>
                                    history.push(
                                      "/dashboard/manage/stock/stock-issuance-dept"
                                    )
                                  }
                                >
                                  <i
                                    className="fa fa-arrow-left"
                                    aria-hidden="true"
                                  ></i>{" "}
                                  {_t(t("Back"))}
                                </button>
                                {!newStockIssuance?.is_post && (
                                  <button
                                    disabled={loading}
                                    type="submit"
                                    className="btn btn-primary rounded"
                                  >
                                    <i
                                      className="fa fa-floppy-o"
                                      aria-hidden="true"
                                    ></i>{" "}
                                    {_t(t("Save"))}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
    </>
  );
};

export default StockIssuanceDeptAction;